import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, DatePicker, Descriptions, Divider, Form, Input, InputNumber, Modal, Row, Select, Space, Spin, Tag, Typography, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '../../api/apiClient';
import API_CONFIG from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { useSelection } from '../../SelectionContext';
import { useEcommercePermissions } from '../../hooks/useEcommercePermissions';

const options = (rows) => (rows || []).map((row) => ({ value: Number(row.id), label: row.name }));
const errorText = (error) => error?.response?.data?.error || error?.message || 'Enregistrement impossible.';
const required = (message) => [{ required: true, message }];

export default function DeliveryFinancePanel({ orderId, orderStatus, onUpdated }) {
  const { selectedCompany, selectedWarehouse } = useSelection();
  const { hasPermission } = useAuth();
  const { canProcessOrders } = useEcommercePermissions();
  const [form] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [messageApi, messageHolder] = message.useMessage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentOptions, setPaymentOptions] = useState({ modes: [], accounts: [] });
  const [paymentError, setPaymentError] = useState('');
  const submitting = useRef(false);
  const uploaded = useRef(null);
  const paymentKey = useRef('');
  const paymentDefaults = useRef(null);
  const scope = String(selectedCompany) + ':' + String(selectedWarehouse) + ':' + orderId + ':' + (orderStatus || '');
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const canProcess = canProcessOrders();
  const canCreate = canProcess && hasPermission('Gestion Commerciale.Depenses.SaisieDepenses.create');
  const canPay = hasPermission('Gestion Commerciale.Depenses.SaisieDepenses.create_payment');
  const canReadFinance = hasPermission('Gestion Commerciale.Depenses.SaisieDepenses.view') ||
    hasPermission('Gestion Commerciale.Depenses.SaisieDepenses.create') || canPay;
  const mode = Form.useWatch('delivery_mode', form);
  const action = Form.useWatch('expense_action', form) || 'none';
  const supplier = Form.useWatch('supplier_id', form);
  const current = data?.delivery;
  const currency = data?.order.currency_code || 'XOF';
  const currencyLabel = ['XOF', 'XAF'].includes(currency) ? 'FCFA' : currency;
  const formatMoney = (value) => value == null ? '—' : Number(value).toLocaleString('fr-FR') + ' ' + currencyLabel;
  const hasExpense = Boolean(current?.expense_id || current?.expense_registered);
  const contextMatches = data && Number(data.order.company_id) === Number(selectedCompany) &&
    Number(data.order.warehouse_id) === Number(selectedWarehouse);
  const locked = saving || !contextMatches || !canProcess || !data?.schema_ready || hasExpense;
  const headers = useCallback(() => ({ 'X-Company-Id': String(selectedCompany) }), [selectedCompany]);

  const refresh = useCallback(async (signal) => {
    const requestedScope = scope;
    setLoading(true); setError('');
    try {
      const response = await apiClient.get(API_CONFIG.apiUrl('/ecommerce/orders/' + orderId + '/delivery-finance'), { headers: headers(), signal });
      if (requestedScope !== scopeRef.current || signal?.aborted) return;
      setData(response.data);
    } catch (err) {
      if (requestedScope === scopeRef.current && !signal?.aborted) { setData(null); setError(errorText(err)); }
    } finally { if (requestedScope === scopeRef.current && !signal?.aborted) setLoading(false); }
  }, [scope, headers, orderId]);

  useEffect(() => {
    if (!data?.schema_ready || data.delivery?.expense_id || !canProcess) return;
    form.setFieldsValue({
      delivery_mode: data.delivery?.delivery_mode,
      supplier_id: data.delivery?.supplier_id || data.default_supplier_id || undefined,
      expense_action: 'none', amount: undefined, expense_id: undefined, expense_category_id: undefined,
      date: dayjs(), due_date: null, bill: 'LIV-' + data.order.order_number, notes: '',
    });
  }, [data, canProcess, form]);
  useEffect(() => {
    if (paymentOpen && !paymentLoading && paymentDefaults.current) {
      paymentForm.setFieldsValue(paymentDefaults.current);
      paymentDefaults.current = null;
    }
  }, [paymentOpen, paymentLoading, paymentForm]);

  useEffect(() => {
    const controller = new AbortController();
    setData(null); setFile(null); uploaded.current = null; paymentDefaults.current = null; setPaymentOpen(false);
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh, canProcess]);

  const save = async (values) => {
    if (submitting.current || locked) return;
    if (mode === 'external' && action !== 'none' && !canCreate) return;
    submitting.current = true; setSaving(true); setError('');
    const requestedScope = scope;
    try {
      const payload = { delivery_mode: values.delivery_mode, supplier_id: values.delivery_mode === 'external' ? values.supplier_id : null };
      if (values.delivery_mode === 'external' && values.expense_action === 'create') {
        Object.assign(payload, { create_expense: true, amount: values.amount, expense_category_id: values.expense_category_id,
          date: values.date.format('YYYY-MM-DD'), due_date: values.due_date?.format('YYYY-MM-DD') || null, bill: values.bill, notes: values.notes });
        if (file) {
          if (!uploaded.current) {
            const upload = new FormData(); upload.append('file', file);
            const response = await apiClient.post(API_CONFIG.apiUrl('/expenses/upload-document'), upload, { headers: { ...headers(), 'Content-Type': 'multipart/form-data' } });
            if (requestedScope !== scopeRef.current) return;
            uploaded.current = response.data;
          }
          payload.document_url = uploaded.current.document_url || uploaded.current.url;
          payload.document_name = uploaded.current.document_name || uploaded.current.name || file.name;
        }
      } else if (values.delivery_mode === 'external' && values.expense_action === 'attach') {
        payload.expense_id = values.expense_id;
      }
      if (requestedScope !== scopeRef.current) return;
      await apiClient.put(API_CONFIG.apiUrl('/ecommerce/orders/' + orderId + '/delivery-finance'), payload, { headers: headers() });
      if (requestedScope !== scopeRef.current) return;
      messageApi.success(payload.create_expense ? 'Dépense de livraison créée, en attente de règlement.' : 'Suivi de livraison enregistré.');
      setFile(null); uploaded.current = null;
      await refresh(); onUpdated?.();
    } catch (err) { if (requestedScope === scopeRef.current) setError(errorText(err)); }
    finally { submitting.current = false; if (requestedScope === scopeRef.current) setSaving(false); }
  };

  const openPayment = async () => {
    if (!canPay || !hasExpense || !contextMatches || saving || Number(current.due_amount) <= 0) return;
    const requestedScope = scope;
    paymentKey.current = 'delivery-expense-' + current.expense_id + '-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    paymentDefaults.current = { amount: Number(current.due_amount), date: dayjs(), payment_mode_id: undefined, treasury_account_id: undefined };
    setPaymentOptions({ modes: [], accounts: [] }); setPaymentError(''); setPaymentOpen(true); setPaymentLoading(true);
    try {
      const [modes, accounts] = await Promise.all([
        apiClient.get(API_CONFIG.apiUrl('/payment-modes'), { headers: headers(), params: { limit: 200 } }),
        apiClient.get(API_CONFIG.apiUrl('/treasury/accounts'), { headers: headers(), params: { company_id: data.order.company_id, warehouse_id: data.order.warehouse_id, module_scope: 'commercial', include_inactive: false } }),
      ]);
      if (requestedScope !== scopeRef.current) return;
      setPaymentOptions({
        modes: (modes.data.paymentModes || []).filter((item) => !item.company_id || Number(item.company_id) === Number(data.order.company_id)),
        accounts: (accounts.data.data || []).filter((item) => Number(item.is_active) === 1),
      });
    } catch (err) { if (requestedScope === scopeRef.current) setPaymentError(errorText(err)); }
    finally { if (requestedScope === scopeRef.current) setPaymentLoading(false); }
  };
  const pay = async (values) => {
    if (submitting.current || !contextMatches || !canPay || !hasExpense) return;
    submitting.current = true; setSaving(true); setPaymentError('');
    const requestedScope = scope;
    try {
      await apiClient.post(API_CONFIG.apiUrl('/payments/process-expense-payment'), {
        expense_id: current.expense_id, company_id: data.order.company_id, warehouse_id: data.order.warehouse_id,
        module_scope: 'commercial', amount: values.amount, date: values.date.format('YYYY-MM-DD'),
        payment_mode_id: values.payment_mode_id, treasury_account_id: values.treasury_account_id,
        notes: 'Règlement livraison ' + data.order.order_number,
      }, { headers: { ...headers(), 'X-Idempotency-Key': paymentKey.current } });
      if (requestedScope !== scopeRef.current) return;
      messageApi.success('Paiement fournisseur enregistré.'); setPaymentOpen(false);
      await refresh(); onUpdated?.();
    } catch (err) { if (requestedScope === scopeRef.current) { setPaymentError(errorText(err)); await refresh(); } }
    finally { submitting.current = false; if (requestedScope === scopeRef.current) setSaving(false); }
  };

  return <section aria-label="Suivi financier de la livraison">
    {messageHolder}
    <Divider orientation="left">Livraison et prestataire</Divider>
    {error && <Alert type="error" showIcon message={error} action={<Button onClick={() => void refresh()}>Actualiser</Button>} />}
    {loading && !data ? <Spin /> : data && !data.schema_ready ? <Alert type="info" showIcon message="Le suivi financier des livraisons doit être activé par un administrateur." /> : data ? <>
      {!contextMatches && <Alert type="warning" showIcon message="Sélectionnez l’entreprise et l’établissement de cette commande." />}
      {hasExpense && (!canReadFinance || data.financial_details_available === false) ? <Descriptions size="small" column={1}>
        <Descriptions.Item label="Prestataire">{current.supplier_name || 'Non renseigné'}</Descriptions.Item>
        <Descriptions.Item label="Dépense de livraison">Enregistrée</Descriptions.Item>
      </Descriptions> : hasExpense ? <>
        <Descriptions size="small" column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Prestataire">{current.supplier_name}</Descriptions.Item>
          <Descriptions.Item label="Référence">{current.bill || '—'}</Descriptions.Item>
          {current.document_url && <Descriptions.Item label="Justificatif"><Typography.Link href={current.document_url} target="_blank" rel="noopener noreferrer">{current.document_name || 'Consulter le document'}</Typography.Link></Descriptions.Item>}
          <Descriptions.Item label="Coût prestataire">{formatMoney(current.amount)}</Descriptions.Item>
          <Descriptions.Item label="Statut fournisseur"><Tag>{current.payment_status || 'Non renseigné'}</Tag></Descriptions.Item>
          <Descriptions.Item label="Payé au prestataire">{formatMoney(current.paid_amount)}</Descriptions.Item>
          <Descriptions.Item label="Reste à payer">{formatMoney(current.due_amount)}</Descriptions.Item>
        </Descriptions>
        {Number(current.due_amount) > 0 && canPay && <Button onClick={() => void openPayment()} disabled={!contextMatches || saving}>Enregistrer un paiement fournisseur</Button>}
        <Typography.Paragraph type="secondary">La dépense reste conservée si la commande est remboursée. Les corrections du prestataire nécessitent leur propre justificatif.</Typography.Paragraph>
      </> : !canProcess ? <Descriptions size="small" column={1}>
        <Descriptions.Item label="Livraison assurée par">{current?.delivery_mode === 'internal' ? 'L’entreprise' : current?.delivery_mode === 'external' ? 'Un prestataire' : 'Non renseigné'}</Descriptions.Item>
        {current?.supplier_name && <Descriptions.Item label="Prestataire">{current.supplier_name}</Descriptions.Item>}
      </Descriptions> : <Form form={form} layout="vertical" onFinish={save} disabled={locked}>
        <Row gutter={16}>
          <Col xs={24} sm={12}><Form.Item name="delivery_mode" label="Livraison assurée par" rules={required('Choisissez le mode de livraison.')}>
            <Select placeholder="Choisir le mode" options={[{ value: 'internal', label: 'L’entreprise' }, { value: 'external', label: 'Un prestataire' }]} /></Form.Item></Col>
          {mode === 'external' && <Col xs={24} sm={12}><Form.Item name="supplier_id" label="Fournisseur prestataire" rules={required('Sélectionnez le fournisseur.')}>
            <Select showSearch optionFilterProp="label" placeholder="Choisir un fournisseur" options={options(data.suppliers)} onChange={() => form.setFieldValue('expense_id', undefined)} /></Form.Item></Col>}
        </Row>
        {mode === 'external' && canCreate && <>
          <Form.Item name="expense_action" label="Dépense de livraison">
            <Select options={[{ value: 'none', label: 'Renseigner uniquement le prestataire' }, { value: 'create', label: 'Créer une dépense fournisseur' },
              ...(data.expenses.length ? [{ value: 'attach', label: 'Rattacher une dépense existante' }] : [])]} /></Form.Item>
          {action !== 'none' && !['shipped', 'delivered', 'refunded', 'cancelled'].includes(data.order.status) && <Alert type="info" showIcon message="Le coût peut être enregistré après confirmation de la prestation, à partir de l’expédition." />}
          {action === 'attach' && <Form.Item name="expense_id" label="Dépense à rattacher" rules={required('Choisissez une dépense.')}>
            <Select showSearch optionFilterProp="label" options={data.expenses.filter((item) => Number(item.supplier_id) === Number(supplier)).map((item) => ({ value: Number(item.id), label: (item.bill || 'Sans référence') + ' · ' + formatMoney(item.amount) }))} /></Form.Item>}
          {action === 'create' && <>
            <Row gutter={16}>
              <Col xs={24} sm={12}><Form.Item name="amount" label={'Coût réel du prestataire (' + currencyLabel + ')'} rules={required('Indiquez le coût du prestataire.')} extra="Ce montant peut différer des frais facturés au client.">
                <InputNumber min={0.01} precision={2} style={{ width: '100%' }} /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item name="expense_category_id" label="Catégorie de dépense" rules={required('Choisissez une catégorie.')}>
                <Select showSearch optionFilterProp="label" options={options(data.categories)} /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item name="date" label="Date de la dépense" rules={required('Choisissez une date.')}><DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item name="due_date" label="Échéance fournisseur"><DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item name="bill" label="Référence du justificatif"><Input maxLength={191} /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item label="Justificatif">
                <Upload accept=".pdf,.jpg,.jpeg,.png" maxCount={1} fileList={file ? [file] : []} beforeUpload={(next) => {
                  if (!['application/pdf', 'image/jpeg', 'image/png'].includes(next.type) || next.size > 10 * 1024 * 1024) { messageApi.error('Choisissez un PDF ou une image de 10 Mo maximum.'); return Upload.LIST_IGNORE; }
                  setFile(next); uploaded.current = null; return false;
                }} onRemove={() => { setFile(null); uploaded.current = null; }}><Button icon={<UploadOutlined />}>Joindre un document</Button></Upload>
              </Form.Item></Col>
            </Row>
            <Form.Item name="notes" label="Notes"><Input.TextArea maxLength={1000} rows={2} /></Form.Item>
          </>}
        </>}
        {canProcess && <Button type="primary" htmlType="submit" loading={saving} disabled={locked || (mode === 'external' && action !== 'none' && !['shipped', 'delivered', 'refunded', 'cancelled'].includes(data.order.status))}>
          {mode === 'external' && action === 'create' ? 'Créer la dépense de livraison' : mode === 'external' && action === 'attach' ? 'Rattacher la dépense' : 'Enregistrer la livraison'}
        </Button>}
      </Form>}
    </> : null}
    <Modal open={paymentOpen} title="Régler le prestataire de livraison" onCancel={() => { if (!saving) setPaymentOpen(false); }} destroyOnClose
      footer={<Space><Button onClick={() => setPaymentOpen(false)} disabled={saving}>Annuler</Button><Button type="primary" loading={saving} disabled={paymentLoading || Boolean(paymentError) || !paymentOptions.accounts.length || !paymentOptions.modes.length || !contextMatches} onClick={() => paymentForm.submit()}>Enregistrer le paiement</Button></Space>}>
      {paymentError && <Alert type="error" showIcon message={paymentError} />}
      {!paymentLoading && !paymentOptions.accounts.length && <Alert type="info" showIcon message="Aucun compte de trésorerie actif disponible pour cet établissement." />}
      {paymentLoading ? <Spin /> : <Form form={paymentForm} layout="vertical" onFinish={pay} disabled={saving || !contextMatches}>
        <Form.Item name="amount" label={'Montant à payer (' + currencyLabel + ')'} rules={required('Indiquez le montant.')}><InputNumber min={0.01} max={Number(current?.due_amount || 0)} precision={2} style={{ width: '100%' }} /></Form.Item>
        <Form.Item name="date" label="Date du paiement" rules={required('Choisissez une date.')}><DatePicker format="DD/MM/YYYY" /></Form.Item>
        <Form.Item name="payment_mode_id" label="Mode de paiement" rules={required('Choisissez un mode.')}><Select options={options(paymentOptions.modes)} /></Form.Item>
        <Form.Item name="treasury_account_id" label="Compte de trésorerie" rules={required('Choisissez un compte.')}><Select options={options(paymentOptions.accounts)} /></Form.Item>
      </Form>}
    </Modal>
  </section>;
}
